import React, { useEffect, useState } from "react";
import { Modal, Button, Row, Col, Divider, Typography, Switch, Tooltip } from "antd";
import { BgColorsOutlined, CloseCircleOutlined } from "@ant-design/icons";
import ColorPickerModal from "../ColorPickerModal/ColorPickerModal";
import { CARD_HEADER_SECTIONS, normalizeCardHeaderColors } from "../../../constant/BoardSections";
import styles from "./CardHeaderColors.module.scss";

const ThemeTile = ({ label, value }) => (
    <div className={styles.themeTile}>
        <span className={styles.themeSwatch} style={{ backgroundColor: value }} />
        <span className={styles.themeMeta}>
            <span className={styles.themeLabel}>{label}</span>
            <Typography.Text className={styles.themeValue}>{value || "—"}</Typography.Text>
        </span>
    </div>
);

const ColorPickButton = ({ label, value, inherited, onPick, onClear }) => {
    const shown = value || inherited;
    return (
        <div className={styles.colorPick}>
            <div className={styles.pickButton} onClick={onPick}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <span
                        className={styles.pickSwatch}
                        style={shown ? { backgroundColor: shown } : { background: "repeating-conic-gradient(#eee 0 25%, #fff 0 50%) 0 0 / 10px 10px" }}
                    />
                    <span>{shown ? shown.toUpperCase() : label}</span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {value && (
                        <Tooltip title="Clear">
                            <CloseCircleOutlined
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClear();
                                }}
                            />
                        </Tooltip>
                    )}
                    <BgColorsOutlined />
                </span>
            </div>
        </div>
    );
};

const CardHeaderColorsModal = ({ visible, onClose, onSave, saving, cardHeaderColors, themePrimary, themeSecondary }) => {
    const [colors, setColors] = useState(normalizeCardHeaderColors(cardHeaderColors));
    const [applyPrimaryToAll, setApplyPrimaryToAll] = useState(false);
    const [applySecondaryToAll, setApplySecondaryToAll] = useState(false);
    const [picker, setPicker] = useState(null);

    useEffect(() => {
        if (visible) {
            const normalized = normalizeCardHeaderColors(cardHeaderColors);
            setColors(normalized);
            setApplyPrimaryToAll(Boolean(normalized.primary_color));
            setApplySecondaryToAll(Boolean(normalized.secondary_color));
        }
    }, [visible, cardHeaderColors]);

    const setSectionColor = (key, field, value) => {
        setColors((prev) => ({
            ...prev,
            sections: {
                ...prev.sections,
                [key]: { ...prev.sections[key], [field]: value },
            },
        }));
    };

    const previewStyle = (entry) => ({
        backgroundColor: entry.background || (applyPrimaryToAll ? themePrimary : "#9e9e9e"),
        color: entry.text || (applySecondaryToAll ? themeSecondary : "#ffffff"),
    });

    const handleSave = () => {
        const payload = {
            primary_color: applyPrimaryToAll ? themePrimary : null,
            secondary_color: applySecondaryToAll ? themeSecondary : null,
            sections: {},
        };
        CARD_HEADER_SECTIONS.forEach(({ key }) => {
            const entry = colors.sections[key];
            if (entry.background || entry.text) {
                payload.sections[key] = { background: entry.background, text: entry.text };
            }
        });
        onSave(payload);
    };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            title="Card Header Colors"
            width={680}
            footer={[
                <Button key="cancel" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>,
                <Button key="save" type="primary" className="standard-btn-color" loading={saving} onClick={handleSave}>
                    Save Colors
                </Button>,
            ]}
        >
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <ThemeTile label="Primary Color (Header Background)" value={themePrimary} />
                </Col>
                <Col span={12}>
                    <ThemeTile label="Secondary Color (Header Text)" value={themeSecondary} />
                </Col>
            </Row>
            <Row gutter={12}>
                <Col span={12}>
                    <div className={styles.switchRow}>
                        <Switch checked={applyPrimaryToAll} onChange={setApplyPrimaryToAll} />
                        <Typography.Text className={styles.switchLabel}>
                            Make primary color for all sections
                        </Typography.Text>
                    </div>
                </Col>
                <Col span={12}>
                    <div className={styles.switchRow}>
                        <Switch checked={applySecondaryToAll} onChange={setApplySecondaryToAll} />
                        <Typography.Text className={styles.switchLabel}>
                            Make secondary color for all section text
                        </Typography.Text>
                    </div>
                </Col>
            </Row>
            <Divider orientation="left">Custom Colors Per Section</Divider>
            <div className={styles.sectionList}>
                {CARD_HEADER_SECTIONS.map(({ key, label, view }) => {
                    const entry = colors.sections[key];
                    return (
                        <div className={styles.sectionRow} key={key}>
                            <div className={styles.sectionInfo}>
                                <span className={styles.sectionPreview} style={previewStyle(entry)}>
                                    {label}
                                </span>
                                <span className={styles.sectionView}>{view}</span>
                            </div>
                            <ColorPickButton
                                label="Background"
                                value={entry.background}
                                inherited={applyPrimaryToAll ? themePrimary : null}
                                onPick={() => setPicker({ key, field: "background" })}
                                onClear={() => setSectionColor(key, "background", null)}
                            />
                            <ColorPickButton
                                label="Text"
                                value={entry.text}
                                inherited={applySecondaryToAll ? themeSecondary : null}
                                onPick={() => setPicker({ key, field: "text" })}
                                onClear={() => setSectionColor(key, "text", null)}
                            />
                        </div>
                    );
                })}
            </div>
            {picker && (
                <ColorPickerModal
                    visible={Boolean(picker)}
                    color={
                        colors.sections[picker.key][picker.field] ||
                        (picker.field === "background" && applyPrimaryToAll ? themePrimary : null) ||
                        (picker.field === "text" && applySecondaryToAll ? themeSecondary : null) ||
                        "#ffffff"
                    }
                    onChange={(color) => setSectionColor(picker.key, picker.field, color)}
                    onClose={() => setPicker(null)}
                    title="Pick a color"
                />
            )}
        </Modal>
    );
};

export default CardHeaderColorsModal;
